import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Layout, { Container } from "../components/layout";
import { apiurl } from "../config/config";
import { SuwasthiLoader } from "../components/loader";
import Pagination from "../components/pagination";

const Candidates = () => {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(100);
  const [totalItems, setTotalItems] = useState(0);

  const navigate = useNavigate();

  const fetchCandidates = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${apiurl}/api/candidate/get?page=${currentPage}&limit=${itemsPerPage}`
      );
      const data = await res.json();
      setCandidates(data?.candidates || []);
      setTotalItems(data?.pagination?.totalItems || 0);
    } catch (err) {
      console.error("Error fetching candidates:", err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage]);

  useEffect(() => {
    fetchCandidates();
  }, [fetchCandidates]);

  // 🔍 Search (client-side filter)
  const filteredCandidates = candidates.filter((candidate) => {
    const firstName = candidate?.personalInfo?.firstName?.toLowerCase() || "";
    const lastName = candidate?.personalInfo?.lastName?.toLowerCase() || "";
    const email = candidate?.personalInfo?.email?.toLowerCase() || "";
    const aadhar = candidate?.aadharNumber || "";

    return (
      firstName.includes(search.toLowerCase()) ||
      lastName.includes(search.toLowerCase()) ||
      email.includes(search.toLowerCase()) ||
      aadhar.includes(search)
    );
  });

  return (
    <Layout>
      <Container overflow="overflow-scroll">
        <div className="p-4 bg-gray-100 min-h-screen">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
            <h2 className="text-2xl font-semibold text-gray-800">
              Candidate List
              <span className="ml-2 text-sm text-gray-500">
                ({totalItems})
              </span>
            </h2>

            <div className="flex flex-col md:flex-row gap-3 items-center">
              <input
                type="text"
                placeholder="Search by Name, Email, Aadhaar"
                className="p-2 border rounded-lg w-full md:w-72 shadow-sm focus:ring focus:ring-blue-200"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />

              {/* Items per page selector */}
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1); // reset page
                }}
                className="border rounded-lg p-2 text-sm"
              >
                {[100, 200, 500, 1000, 2000].map((limit) => (
                  <option key={limit} value={limit}>
                    {limit} / page
                  </option>
                ))}
              </select>
            </div>
          </div>

          {loading ? (
            <SuwasthiLoader maintext="Fetching Candidates..." />
          ) : (
            <div className="bg-white shadow-md rounded-xl p-4 overflow-x-auto">
              {filteredCandidates.length > 0 ? (
                <table className="w-full text-sm text-left text-gray-600">
                  <thead className="bg-gray-100 text-gray-700 text-xs uppercase">
                    <tr>
                      <th className="p-3">Candidate ID</th>
                      <th className="p-3">Name</th>
                      <th className="p-3">Email</th>
                      <th className="p-3">Aadhaar Number</th>
                      <th className="p-3">Applied</th>
                      <th className="p-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCandidates.map((candidate, index) => (
                      <tr
                        key={candidate._id}
                        className={`border-b hover:bg-gray-50 transition ${
                          index % 2 === 0 ? "bg-white" : "bg-gray-50"
                        }`}
                      >
                        <td className="p-3">{candidate?.id}</td>
                        <td className="p-3 font-medium text-gray-900">
                          {candidate?.personalInfo?.firstName}{" "}
                          {candidate?.personalInfo?.lastName}
                        </td>
                        <td className="p-3">{candidate?.personalInfo?.email}</td>
                        <td className="p-3">{candidate?.aadharNumber}</td>
                        <td className="p-3">{candidate?.appliedPositions?.length}</td>
                        <td className="p-3 text-right">
                          <button
                            className="bg-blue-500 text-white px-3 py-1 rounded-lg text-xs hover:bg-blue-600 transition"
                            onClick={() =>
                              navigate(`/candidates/${candidate?._id}`)
                            }
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-center text-gray-500 py-6">
                  No candidates found.
                </p>
              )}
            </div>
          )}

          {/* Pagination */}
          <div className="mt-6">
            <Pagination
              currentPage={currentPage}
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              onPageChange={(page) => setCurrentPage(page)}
            />
          </div>
        </div>
      </Container>
    </Layout>
  );
};

export default Candidates;